import React, { Fragment, useState, memo, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import cookie from 'react-cookie';
import format from 'date-fns/format';
import addYears from 'date-fns/addYears';

import { Button, Glyphicon, Form, FormGroup, Checkbox } from 'react-bootstrap';

import {
  currencySymbol,
  isHighSupplyLocation,
  moreExamplesNeededToPitch,
  pluralOrSingular,
} from 'helpers/HelperFunctions';
import { computeUserRank } from 'helpers/UserRank';
import featureToggles from 'featureToggles';
import { load as loadSubscriptionPortalUrl } from 'redux/modules/subscriptions';

import DatePicker from 'components/Shared/DatePicker';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import FormSelect from 'components/FormSelect/FormSelect';
import ImproveRanking from 'components/BriefDetailsFreelancer/CreativePitch/ImproveRanking/ImproveRanking';
import Panel from 'components/Panel/Panel';
import styles from './CreativePitch.module.scss';
import CreativePitchUserCard from 'components/BriefDetailsFreelancer/CreativePitch/CreativePitchUserCard';
import { MINIMUM_INITIAL_PITCH_AMOUNT } from 'helpers/globalVariables';
import Modal from 'components/Modal/Modal';
import Loader from 'components/Loader/Loader';
import { trackEvent } from 'helpers/Analytics';

const CreativePitch = ({
  availabilityToggle,

  briefCurrency,
  briefRole,
  briefPaid,

  briefRemote,
  briefLocation,
  creativeJob,

  creditsRoles,

  introsLeft,
  introsLoading,

  pitchText,
  pitchAmount,
  submittingPitch,
  totalPitches,
  pitchError,

  onChangeAvailability,
  onChangeAvailabilityToggle,
  onChangePitchAmount,
  onChangePitchText,
  onGeneratePitch,
  onImportLastPitch,
  onSubmitPitch,

  loadingGeneratedPitch,

  locationCheckbox,
  onLocationCheckboxClick,
  onConfirmationCheckboxClick,
  authUserWasInvited,
  onAttachUpload,

  user,
}) => {
  const dispatch = useDispatch();
  const TODAY = new Date();
  const [date, setDate] = useState(new Date(user.available_at || TODAY));
  const [approxProjectFee, setApproxProjectFee] = useState(0);
  const [feeInputVisibility, setFeeInputVisibility] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const userJoined = new Date(user.joined);
  const nextRenewalDate = new Date(
    TODAY.getFullYear(),
    TODAY.getMonth(),
    userJoined.getDate()
  );
  if (nextRenewalDate <= TODAY)
    nextRenewalDate.setMonth(nextRenewalDate.getMonth() + 1);
  const pitchLimitRenewal = Math.floor(
    (nextRenewalDate - TODAY) / (1000 * 60 * 60 * 24)
  );

  const { subscriptionUrl } = useSelector((state) => state.subscriptions);

  useEffect(() => {
    onChangePitchAmount(0);
    if (user && user.subscribed) {
      dispatch(loadSubscriptionPortalUrl());
    }
  }, []);

  const hasBio = user.bio !== '';

  const lacksRelevantProjects =
    creativeJob && moreExamplesNeededToPitch(briefRole, creditsRoles);

  // const shouldShowLacksProjects = lacksRelevantProjects[1] > 0;
  const shouldShowLacksProjects = false;

  // if lacksRelevantProjects returns false, set to empty Array of length 2
  const [required, remaining] = lacksRelevantProjects || Array(2);

  const hasCreativeRole = () => {
    const creativeRoles = [
      'Content Creator',
      'Actor',
      'Social Media Freelancer',
      'Social Media Manager',
      'Content Producer',
      'UGC Creator',
      'Digital Marketer',
      'Actress',
      'Presenter',
      'Community Manager',
      'Influencer Marketing Manager',
    ];
    // Check if user has featured_roles and if any of them match the creative roles
    return (
      user &&
      user.featured_roles &&
      user.featured_roles.length > 0 &&
      user.featured_roles.some((role) => creativeRoles.includes(role))
    );
  };

  const getSubscriptionUrl = () => {
    if (user && subscriptionUrl && user.subscribed && user.subscription_id) {
      return (
        subscriptionUrl + '/subscriptions/' + user.subscription_id + '/update'
      );
    }

    if (subscriptionUrl) {
      return subscriptionUrl;
    }

    return `https://billing.stripe.com/p/login/4gw5kmbsI0Cb1q0cMM?prefilled_email=${encodeURIComponent(
      user.email
    )}`;
  };

  const lackBioMessage = (
    <Fragment>
      <p>
        <strong>You need to add a bio</strong>
      </p>
      <p>Fill out a short bio on your profile.</p>
    </Fragment>
  );
  const lackProjectsMessage = (
    <Fragment>
      <p>
        <strong>You need a medium profile score</strong> and{' '}
        {briefRole.toLowerCase()} jobs require <strong>{required}</strong>{' '}
        relevant profile {pluralOrSingular(required, 'example')}.
      </p>
      <p>
        Add <strong>{remaining}</strong> more{' '}
        {pluralOrSingular(remaining, 'example')} to contact this client.
      </p>
    </Fragment>
  );

  const activateAccount = (
    <Panel
      className="box-shadow"
      style={{
        padding: '30px 20px',
      }}
    >
      <p className="text-center">
        <SvgIcon icon="email" color="#0777DA" size="48" />
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Lato',
          fontWeight: '700',
          fontSize: '20px',
        }}
      >
        <strong>Confirm your email</strong>
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Lato',
          fontSize: '16px',
          maxWidth: '350px',
          margin: 'auto',
        }}
      >
        Twine has sent you an activation email.
        <br />
        Open your email and <b>click the link</b> to activate your account
        before you can apply.
        <br />
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Lato',
          fontSize: '14px',
          color: '#666',
          marginTop: '20px',
        }}
      >
        Once activated,{' '}
        <a href="#" onClick={() => window.location.reload()}>
          reload the page
        </a>
        .
      </p>
    </Panel>
  );

  const maximumPitchLimit = (
    <Panel
      className="box-shadow"
      style={{
        padding: '30px 20px',
      }}
    >
      <p className="text-center">
        <SvgIcon icon="clock" color="#0777DA" size="48" />
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Lato',
          fontWeight: '700',
          fontSize: '20px',
        }}
      >
        <strong>Free applications limit reached</strong>
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Lato',
          fontSize: '16px',
        }}
      >
        Each job receives a limited amount of applications from free accounts.
        <br />
        This job has hit that limit.
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Lato',
          fontSize: '16px',
        }}
      >
        Still want to apply?{' '}
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Lato',
          fontSize: '16px',
        }}
      >
        <Link to="/pricing/creative">
          <Button bsStyle="primary">Upgrade to Twine Pro</Button>
        </Link>
      </p>
      {cookie.load('utm_campaign') === 'linkedin_job' ? (
        <div className={styles.discountUpsell}>
          🎁&nbsp;&nbsp;&nbsp;Use code <strong>WORK100</strong> to try Pro for
          free (monthly plan).
        </div>
      ) : (
        featureToggles.creatorPartnership &&
        !user.pro &&
        hasCreativeRole() && (
          <div className={styles.discountUpsell}>
            Want a free month of Twine Pro? Contribute to the Twine community
            and earn rewards.{' '}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://twinehq.notion.site/Twine-Community-Contributions-2093bde70b248047823fd85f0318a8ee?source=copy_link/"
            >
              Learn how it works.
            </a>
          </div>
        )
      )}
    </Panel>
  );

  const noMorePitches = (
    <Panel
      className="box-shadow"
      style={{
        padding: '30px 20px',
      }}
    >
      <p className="text-center">
        <SvgIcon icon="clock" color="#0777DA" size="48" />
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Lato',
          fontWeight: '700',
          fontSize: '20px',
        }}
      >
        <strong>Your applications limit reached</strong>
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Lato',
          fontSize: '16px',
        }}
      >
        You have reached your limited amount of monthly applications on your
        account.
        <br />
        {!user.pro ? (
          <>
            <a href="/pricing/creative" target="_blank" rel="noreferrer">
              Upgrade your account
            </a>{' '}
            for more pitches
          </>
        ) : (
          !user.premium && (
            <>
              <a
                href={
                  getSubscriptionUrl()
                    ? getSubscriptionUrl()
                    : '/pricing/creative'
                }
                target="_blank"
                rel="noreferrer"
              >
                Upgrade to Premium
              </a>{' '}
              for no pitch limits
            </>
          )
        )}
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Lato',
          fontSize: '16px',
        }}
      >
        Still want to apply?{' '}
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Lato',
          fontSize: '16px',
        }}
      >
        {!user.pro ? (
          <Link to="/pricing/creative">
            <Button bsStyle="primary">Upgrade your account</Button>
          </Link>
        ) : (
          !user.premium && (
            <Button
              href={
                getSubscriptionUrl()
                  ? getSubscriptionUrl()
                  : '/pricing/creative'
              }
              bsStyle="primary"
            >
              Upgrade to Twine Premium
            </Button>
          )
        )}
      </p>
      {cookie.load('utm_campaign') === 'linkedin_job' ? (
        <div className={styles.discountUpsell}>
          🎁&nbsp;&nbsp;&nbsp;Use code <strong>WORK100</strong> to try Pro for
          free (monthly plan).
        </div>
      ) : (
        featureToggles.creatorPartnership &&
        !user.pro &&
        hasCreativeRole() && (
          <div className={styles.discountUpsell}>
            Want a free month of Twine Pro? Contribute to the Twine community
            and earn rewards.{' '}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://twinehq.notion.site/Twine-Community-Contributions-2093bde70b248047823fd85f0318a8ee?source=copy_link/"
            >
              Learn how it works.
            </a>
          </div>
        )
      )}
    </Panel>
  );

  const pitchAmountOptions = [
    {
      label: 'Need to discuss',
      value: 0,
    },
    {
      label:
        currencySymbol(briefCurrency, false) +
        MINIMUM_INITIAL_PITCH_AMOUNT.toString(),
      value: MINIMUM_INITIAL_PITCH_AMOUNT,
    },
    { label: currencySymbol(briefCurrency, false) + '100', value: 100 },
    { label: currencySymbol(briefCurrency, false) + '250', value: 250 },
    { label: currencySymbol(briefCurrency, false) + '500', value: 500 },
    { label: currencySymbol(briefCurrency, false) + '1000', value: 1000 },
    { label: currencySymbol(briefCurrency, false) + '5000', value: 5000 },
    { label: currencySymbol(briefCurrency, false) + '10000', value: 10000 },
    { label: 'Other amount', value: null },
  ];

  const handleApproxPitchFee = (approxFee) => {
    if (approxFee === 'Other amount') {
      setFeeInputVisibility(true);
    } else {
      setFeeInputVisibility(false);
      onChangePitchAmount(approxFee);
      setApproxProjectFee(approxFee);
    }
  };

  const pitchBoxCriteria = () => {
    // If the user was invited
    if (authUserWasInvited) {
      return true;
    }

    // If the user is a pro user or premium
    if (user.premium) {
      return true;
    }
    if (user.pro && user.messages_count > 0) {
      return true;
    }

    // Not a popular job then allow pitch
    if (
      totalPitches <= 15 &&
      user.messages_count > 0 &&
      user.email_verified &&
      hasBio
    ) {
      return true;
    }

    return false;
  };

  const rankUpCriteria = () => {
    const fromHighSupplyLocation = isHighSupplyLocation(user.location.text);

    if (user.premium) return false;
    if (user.pro && user.messages_count > 0) return false;

    if (pitchBoxCriteria()) return false;

    if (!pitchBoxCriteria() && !authUserWasInvited) return true;

    if (!user.email_verified) return true;

    if (!hasBio) return true;

    if (shouldShowLacksProjects) return true;

    // Popular job
    if (totalPitches > 15 && !user.pro && !user.premium) return true;

    if (
      computeUserRank(user) < 3 &&
      (totalPitches >= 1 || fromHighSupplyLocation)
    )
      return true;

    return false;
  };

  const displayRankUpCriteria = rankUpCriteria();

  const displayPitchBoxCriteria = pitchBoxCriteria();

  return (
    <div id="pitch-section">
      {/* Freelancer Validation Rules */}
      {displayRankUpCriteria &&
        (!user.email_verified ? (
          activateAccount
        ) : !hasBio ? (
          <ImproveRanking
            requiredRank="medium"
            message={lackBioMessage}
            profileLink={user.links.main_relative}
          />
        ) : shouldShowLacksProjects ? (
          //  Portfolio Projects - Medium Rank Requirement
          <ImproveRanking
            message={lackProjectsMessage}
            profileLink={user.links.main_relative}
          />
        ) : user.messages_count === 0 ? (
          noMorePitches
        ) : totalPitches > 15 ? (
          // Popular Job Pitch Limit
          maximumPitchLimit
        ) : (
          computeUserRank(user) < 3 &&
          (totalPitches >= 1 ||
            (isHighSupplyLocation(user.location.text) && (
              // Verified Freelancers Check - High Rank Requirement
              <ImproveRanking
                requiredRank="high"
                profileLink={user.links.main_relative}
              />
            )))
        ))}

      {/* Freelancer Pitch Components */}
      {displayPitchBoxCriteria && (
        <div className={`${styles.container} box-shadow`}>
          {/* Freelancer Pitch Message */}
          <div className={styles.pitchTitleContainer}>
            <h3>Easy Apply ⚡️</h3>
          </div>
          {loadingGeneratedPitch ? (
            <div className={styles.loadingPitchContainer}>
              <h3 className={styles.loadingPitchTitle}>
                ✨ Generating your application
              </h3>
              <p className={styles.loadingPitchText}>
                Hang tight whilst AI generates your application
              </p>
              <Loader loading />
            </div>
          ) : (
            <>
              <textarea
                className={`form-control ${
                  pitchError && pitchError.pitchText && styles.error
                }`}
                style={{ resize: 'vertical' }}
                disabled={submittingPitch}
                maxLength="10000"
                onChange={(event) => onChangePitchText(event.target.value)}
                placeholder={`To apply, write a short introduction. Keep it friendly and relevant to this job.\n\nYou can use markdown:\n**Bold text** and _italic text_\n[this is a link](www.example.com) and - for bullet points`}
                rows="5"
                value={pitchText}
              />
              {pitchError && pitchError.pitchText && (
                <div className={styles.errorMsg}>{pitchError.pitchText}</div>
              )}
            </>
          )}
          <div className={styles.pitchButtonsContainer}>
            <div className={styles.exampleProjectButtons}>
              <Button
                title="Attach an example from your portfolio"
                bsStyle="info"
                onClick={() => onAttachUpload()}
                disabled={submittingPitch || loadingGeneratedPitch}
              >
                ➕ Portfolio example
              </Button>
            </div>
            <div className={styles.helpPitchButtons}>
              <Button
                title="Import your most recent application"
                bsStyle="info"
                onClick={() => onImportLastPitch()}
                disabled={submittingPitch || loadingGeneratedPitch}
              >
                🔄 Last application
              </Button>
              <Button
                bsStyle="info"
                onClick={() => {
                  trackEvent('user-clicked-ai-pitch');
                  user.pro ? onGeneratePitch() : setShowProModal(true);
                }}
                disabled={submittingPitch || loadingGeneratedPitch}
              >
                ✨ Write this for me
              </Button>
            </div>
          </div>

          {/* No Upsell Partner as of 01/08/2023 */}
          {/* <PitchUpsell
          description={pitchUpsellAdvert.description}
            link={pitchUpsellAdvert.link}
            cta={pitchUpsellAdvert.linkCta}
          /> */}

          {/* Availability, Project Fee, Day Rate, Deposit Rate */}
          {briefPaid && (
            <Fragment>
              <hr />
              {/* Freelancer Availability */}
              <div className={styles.availabilityContainer}>
                <label
                  htmlFor="availabilityInput"
                  className={styles.availabilityLabel}
                >
                  Availability:
                </label>

                <FormSelect
                  id="availabilityInput"
                  className={styles.availabilityInput}
                  disabled={submittingPitch}
                  onChange={onChangeAvailabilityToggle}
                  options={[
                    { label: 'Immediately', value: 0 },
                    { label: 'Starting from...', value: 1 },
                  ]}
                  value={availabilityToggle}
                />
              </div>
              {availabilityToggle === 1 && (
                <div className={styles.datePickerContainer}>
                  <DatePicker
                    dateFormat="do MMMM"
                    className={`form-control ${styles.datePicker}`}
                    minDate={TODAY}
                    maxDate={addYears(TODAY, 1)}
                    placeholderText="Select availability"
                    onChange={(date) => {
                      setDate(date);

                      onChangeAvailability(format(date, 'yyyy-MM-dd HH:mm:ss'));
                    }}
                    selected={date}
                  />
                </div>
              )}

              {pitchError && pitchError.pitchAvailability && (
                <div className={styles.errorMsg}>
                  {pitchError.pitchAvailability}
                </div>
              )}

              <>
                <div className={styles.availabilityContainer}>
                  <label
                    htmlFor="availabilityInput"
                    className={styles.availabilityLabel}
                  >
                    Approximate Project Fee:
                  </label>
                  <FormSelect
                    id="availabilityInput"
                    className={styles.availabilityInput}
                    disabled={submittingPitch}
                    onChange={(e) => handleApproxPitchFee(e)}
                    options={pitchAmountOptions}
                    value={
                      feeInputVisibility ? 'Other amount' : approxProjectFee
                    }
                  />
                </div>

                {feeInputVisibility === true && (
                  <div className={styles.feeContainer}>
                    <label className={styles.feeLabel}></label>
                    <input
                      className={`form-control ${styles.feeInput} ${
                        pitchError && pitchError.pitchAmount && styles.error
                      }`}
                      disabled={submittingPitch}
                      onChange={(event) =>
                        onChangePitchAmount(event.target.value)
                      }
                      placeholder={`${currencySymbol(briefCurrency, false)}500`}
                      e
                      type="number"
                      value={pitchAmount}
                    />
                  </div>
                )}

                {pitchError && pitchError.pitchAmount && (
                  <div
                    className={`${styles.errorMsg} ${styles.feeErrorContainer}`}
                  >
                    <div className={`${styles.errorMsg} ${styles.feeError}`}>
                      {pitchError.pitchAmount}
                    </div>
                  </div>
                )}

                {pitchError && pitchError.pitchDeposits && (
                  <div className={styles.errorMsg}>
                    {pitchError.pitchDeposits}
                  </div>
                )}
              </>

              {!briefRemote && (
                <div className={styles.locationCheckboxContainer}>
                  <Form>
                    <FormGroup>
                      <Checkbox
                        checked={locationCheckbox}
                        onClick={onLocationCheckboxClick}
                        className={styles.locationCheckbox}
                      >
                        <p
                          className={styles.checkboxCopy}
                        >{` I am available to travel to ${briefLocation}`}</p>
                        {pitchError && pitchError.locationCheckbox && (
                          <div className={styles.errorMsg}>
                            {pitchError.locationCheckbox}
                          </div>
                        )}
                      </Checkbox>
                    </FormGroup>
                  </Form>
                </div>
              )}
            </Fragment>
          )}

          {/* Terms */}
          {briefPaid && user && (
            <>
              <hr />
              <div className={styles.payoutDetailsContainer}>
                <Form>
                  <FormGroup>
                    <Checkbox
                      onClick={() => onConfirmationCheckboxClick('terms')}
                    >
                      I agree to the{' '}
                      <a href="/platform-terms" target="_blank">
                        Platform Terms
                      </a>{' '}
                      to work with this client
                    </Checkbox>
                  </FormGroup>
                </Form>
                {pitchError && pitchError.termsConfirmation && (
                  <div className={styles.checkboxError}>
                    <div className={styles.errorMsg}>
                      {pitchError.termsConfirmation}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Contact Client Button */}
          <Button
            bsStyle="primary"
            bsSize="lg"
            className={styles.submitButton}
            disabled={submittingPitch}
            onClick={() => onSubmitPitch()}
          >
            {submittingPitch ? (
              <em>Sending application...</em>
            ) : (
              <>
                Send application
                <Glyphicon className={styles.externalGlyphIcon} glyph="send" />
              </>
            )}
          </Button>

          {/* Remaining Pitches */}
          <p
            style={{
              fontSize: '0.9em',
              marginTop: 0,
              overflow: 'hidden',
              maxHeight: !introsLoading ? '20px' : 0,
              paddingLeft: '10px',
              paddingRight: '10px',
              textAlign: 'center',
            }}
          >
            {introsLeft > 0 && <span>This counts as 1 pitch.</span>}{' '}
            {introsLeft} pitch{introsLeft === 1 ? '' : 'es'} left for next{' '}
            {pitchLimitRenewal} days{' '}
            <a
              target="_blank"
              href="https://help.twine.net/en/article/what-are-pitches-jiedg8/"
              title="Intro and pitch limits"
              rel="noreferrer"
            >
              <SvgIcon icon="help" color="#aaa" size="14" />
            </a>
          </p>
          {!user.pro && (
            <p
              style={{
                fontSize: '0.9em',
                marginTop: 0,
                overflow: 'hidden',
                maxHeight: !introsLoading ? '20px' : 0,
                paddingLeft: '10px',
                paddingRight: '10px',
                textAlign: 'center',
              }}
            >
              <Link to="/pricing/creative">Upgrade to Twine Pro</Link> to
              increase to 50 monthly pitches.
            </p>
          )}
        </div>
      )}

      {/* Preview of user card */}
      {(displayPitchBoxCriteria &&
        user &&
        (!user.bio ||
          user.bio == '' ||
          !user.top_roles ||
          (user.top_roles && user.top_roles.length == 0) ||
          !user.location ||
          (user.location && user.location.text == ''))) ||
        (pitchText !== '' && (
          <>
            <Panel
              className="f f-row ac-center jc-sb"
              style={{
                padding: '10px 20px 0px 20px',
              }}
            >
              <p className="fs-16 lato-bold" style={{ margin: 0 }}>
                Application preview
              </p>
              <Link
                className="pull-right mt--"
                to={`${user.links.main_relative}`}
              >
                Edit Portfolio
              </Link>
            </Panel>

            {/* Freelancer Profile Preview */}
            <CreativePitchUserCard
              user={user}
              pitch={{ text: pitchText }}
              preferredRole={briefRole}
            />
          </>
        ))}

      <Modal
        closeButton
        show={showProModal}
        title={'Upgrade to Pro'}
        onExit={() => setShowProModal(false)}
      >
        <div className={styles.aiPitchModal}>
          <p>
            Save time with a customised application written by AI. This feature
            is only available to Twine Pro members
          </p>
          <div style={{ textAlign: 'center' }}>
            <Button href="/pricing/creative" bsStyle="primary">
              Upgrade to Pro
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

CreativePitch.propTypes = {
  availabilityToggle: PropTypes.number,
  briefCurrency: PropTypes.string,
  briefRole: PropTypes.string,
  briefPaid: PropTypes.bool,
  briefRemote: PropTypes.bool,
  briefLocation: PropTypes.string,
  creativeJob: PropTypes.bool,
  creditsRoles: PropTypes.array,
  introsLeft: PropTypes.number,
  introsLoading: PropTypes.bool,
  pitchText: PropTypes.string,
  pitchAmount: PropTypes.string,
  submittingPitch: PropTypes.bool,
  totalPitches: PropTypes.number,
  pitchError: PropTypes.object,
  onChangeAvailability: PropTypes.func,
  onChangeAvailabilityToggle: PropTypes.func,
  onChangePitchAmount: PropTypes.func,
  onChangePitchText: PropTypes.func,
  onGeneratePitch: PropTypes.func,
  onImportLastPitch: PropTypes.func,
  onSubmitPitch: PropTypes.func,
  loadingGeneratedPitch: PropTypes.bool,
  locationCheckbox: PropTypes.bool,
  onLocationCheckboxClick: PropTypes.func,
  onConfirmationCheckboxClick: PropTypes.func,
  authUserWasInvited: PropTypes.bool,
  onAttachUpload: PropTypes.func,
  user: PropTypes.object,
};

export default memo(CreativePitch);
